import { Button, Callout, Dialog, DialogCancel, Textarea } from '@fruuit/ui'
import { Eye } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { errorMessage } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { browser } from '../auth/api'
import { useStepUp } from '../auth/StepUp'
import { adminApi } from './api'

export interface ViewAsTarget {
  id: string
  display_name: string
  email: string
}

const DEFAULT_REASON = 'Walking through the investor view in a demo'

/**
 * Operator impersonation launcher (P-ADM-012, M-04): a reason, then a 60-minute session
 * as the user. A full page load swaps every cached query to the viewed user.
 */
export function ViewAsDialog({
  target,
  onClose,
}: {
  target: ViewAsTarget | null
  onClose: () => void
}) {
  const [reason, setReason] = useState(DEFAULT_REASON)
  const [error, setError] = useState<string | undefined>()
  const { guard, dialog } = useStepUp()
  const start = useApiMutation(
    (vars: { user_id: string; reason: string }, ctx) =>
      guard(() => adminApi.impersonate(vars, ctx)),
    { onSuccess: (result) => browser.assign(result.redirect) },
  )

  useEffect(() => {
    if (target) {
      setReason(DEFAULT_REASON)
      setError(undefined)
    }
  }, [target])

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!target) return
    if (reason.trim().length < 8) {
      setError('Give a reason of at least 8 characters. It goes to the audit log.')
      return
    }
    setError(undefined)
    start.mutate({ user_id: target.id, reason: reason.trim() })
  }

  return (
    <>
      <Dialog
        open={target !== null}
        onClose={onClose}
        size="sm"
        title={target ? `View as ${target.display_name}` : 'View as'}
        description="You'll see the portal exactly as this user does for 60 minutes. Exit from the amber banner to return to your own session."
        footer={
          <>
            <DialogCancel />
            <Button type="submit" form="view-as-form" icon={<Eye />} loading={start.isPending}>
              Start viewing
            </Button>
          </>
        }
      >
        <form id="view-as-form" onSubmit={onSubmit} className="flex flex-col gap-4">
          {target ? <p className="text-sm text-sage">{target.email}</p> : null}
          <Textarea
            label="Reason"
            hint="Recorded on the platform audit log."
            name="reason"
            rows={3}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={error}
          />
          <Callout tone="note">
            MFA, linked identities and invitations stay locked while you view as someone else.
          </Callout>
          {start.error ? (
            <p role="alert" className="text-sm text-danger">
              {errorMessage(start.error)}
            </p>
          ) : null}
        </form>
      </Dialog>
      {dialog}
    </>
  )
}
